import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronUp, MessageCircle, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const COLUMNS = ["available", "in_development", "to_vote"];
const COLUMN_ACCENTS = { available: "#34C759", in_development: "#4ba3b7", to_vote: "#dde165" };

function CommentThread({ itemId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = () => api.get(`/roadmap/${itemId}/comments`).then(({ data }) => setComments(data));
  useEffect(() => { load(); }, [itemId]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/roadmap/${itemId}/comments`, { comment: text });
      setText("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setPosting(false);
    }
  };

  if (!comments) return <Loader2 className="h-4 w-4 animate-spin text-[#4ba3b7]" />;

  return (
    <div className="mt-3 pt-3 border-t border-black/5 space-y-2" data-testid={`roadmap-comments-${itemId}`}>
      {comments.map((c) => (
        <p key={c.id} className="text-xs text-[#2B2B2B]/60">{c.comment}</p>
      ))}
      {user && (
        <div className="flex gap-2 pt-1">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("roadmap.commentPlaceholder")}
                 className="h-9 rounded-lg text-xs" data-testid={`roadmap-comment-input-${itemId}`} />
          <Button size="sm" onClick={post} disabled={posting} className="h-9 rounded-lg bg-[#4ba3b7] hover:bg-[#3D8A9A]" data-testid={`roadmap-comment-submit-${itemId}`}>
            {t("common.submit")}
          </Button>
        </div>
      )}
    </div>
  );
}

function RoadmapCard({ item, onVote }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-5" data-testid={`roadmap-card-${item.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-semibold text-sm">{item.title}</h3>
        <button
          onClick={() => user && onVote(item.id)}
          disabled={!user}
          className={`flex flex-col items-center px-2 py-1 rounded-lg border ${item.voted ? "bg-[#4ba3b7]/10 border-[#4ba3b7]/30 text-[#4ba3b7]" : "border-black/10 text-[#2B2B2B]/60"}`}
          data-testid={`roadmap-vote-button-${item.id}`}
        >
          <ChevronUp className="h-4 w-4" />
          <span className="text-xs font-bold">{item.votes_count}</span>
        </button>
      </div>
      {item.description && <p className="text-xs text-[#2B2B2B]/60 mb-2">{item.description}</p>}
      <button onClick={() => setShowComments(!showComments)} className="flex items-center gap-1 text-xs text-[#2B2B2B]/40 hover:text-[#4ba3b7]" data-testid={`roadmap-toggle-comments-${item.id}`}>
        <MessageCircle className="h-3.5 w-3.5" /> {t("roadmap.comments")}
      </button>
      {showComments && <CommentThread itemId={item.id} />}
    </div>
  );
}

export default function RoadmapPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [proposing, setProposing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => api.get("/roadmap").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const handleVote = async (itemId) => {
    try {
      await api.post(`/roadmap/${itemId}/vote`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    }
  };

  const handlePropose = async () => {
    if (!title.trim()) return;
    setProposing(true);
    try {
      await api.post("/roadmap", { title, description });
      toast.success(t("roadmap.ideaSubmitted"));
      setTitle("");
      setDescription("");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail, t));
    } finally {
      setProposing(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="roadmap-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-2">{t("roadmap.title")}</h1>
          <p className="text-sm text-[#2B2B2B]/60">{t("roadmap.subtitle")}</p>
        </div>
        {user && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-[#4ba3b7] hover:bg-[#3D8A9A] gap-2" data-testid="roadmap-propose-idea-button">
                <Plus className="h-4 w-4" /> {t("roadmap.proposeIdea")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>{t("roadmap.proposeIdea")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("roadmap.ideaTitlePlaceholder")}
                       className="h-11 rounded-xl" data-testid="roadmap-idea-title-input" />
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("roadmap.ideaDescriptionPlaceholder")}
                          className="rounded-xl" data-testid="roadmap-idea-description-input" />
              </div>
              <DialogFooter>
                <Button onClick={handlePropose} disabled={proposing || !title.trim()} className="bg-[#4ba3b7] hover:bg-[#3D8A9A] rounded-xl" data-testid="roadmap-idea-submit-button">
                  {proposing ? t("common.loading") : t("common.submit")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!items ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#4ba3b7]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {COLUMNS.map((col) => (
            <div key={col} data-testid={`roadmap-column-${col}`}>
              <div className="flex items-center gap-2 mb-4">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLUMN_ACCENTS[col] }} />
                <h2 className="font-semibold text-sm">{t(`roadmap.status_${col}`)}</h2>
              </div>
              <div className="space-y-3">
                {items.filter((i) => i.status === col).map((item) => (
                  <RoadmapCard key={item.id} item={item} onVote={handleVote} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
